import React, { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Upload, FileText, FilePdf, FileXls, FileCsv, X, Check,
  ArrowRight, CloudArrowUp, Warning, Sparkle, Info, CircleNotch
} from '@phosphor-icons/react';
import { API, useAuth } from '@/App';

const UniverseUpload = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [parseResult, setParseResult] = useState(null);

  // Accepted file types for universe import (Excel and CSV only)
  const acceptedTypes = {
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { icon: FileXls, label: 'Excel', color: 'text-emerald-400' },
    'application/vnd.ms-excel': { icon: FileXls, label: 'Excel', color: 'text-emerald-400' },
    'text/csv': { icon: FileCsv, label: 'CSV', color: 'text-blue-400' },
    'application/csv': { icon: FileCsv, label: 'CSV', color: 'text-blue-400' }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      validateAndSetFile(droppedFiles[0]);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length > 0) {
      validateAndSetFile(selectedFiles[0]);
    }
  };

  const validateAndSetFile = (selectedFile) => {
    const isValid = selectedFile.name.endsWith('.csv') || 
                    selectedFile.name.endsWith('.xlsx') ||
                    selectedFile.name.endsWith('.xls');
    
    if (!isValid) {
      toast.error('Please upload an Excel (.xlsx, .xls) or CSV file');
      return;
    }
    
    setFile(selectedFile);
    setParseResult(null);
  };

  const removeFile = () => {
    setFile(null);
    setParseResult(null);
    setUploadProgress(0);
  };

  const getFileIcon = (f) => {
    if (f.name.endsWith('.xlsx') || f.name.endsWith('.xls')) {
      return <FileXls size={28} className="text-emerald-400" weight="fill" />;
    }
    if (f.name.endsWith('.csv')) {
      return <FileCsv size={28} className="text-blue-400" weight="fill" />;
    }
    return <FileText size={28} className="text-text-tertiary" weight="fill" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file to upload');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/universe/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      setParseResult(response.data);
      toast.success(`Successfully imported ${response.data.regulation_count} regulations!`);
      
      // Navigate to the regulatory universe page after a short delay
      setTimeout(() => {
        navigate(`/universe/${response.data.universe_id}`);
      }, 1500);
      
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload and parse file');
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const handleSkipToGenerate = () => {
    // User decides to generate instead of upload
    const intakeId = location.state?.intakeId;
    navigate('/navi-intro', { state: { intakeId, fromQuestionnaire: true } });
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl w-full"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring" }}
            className="inline-flex items-center justify-center w-16 h-16 bg-gold/20 rounded-full mb-4"
          >
            <CloudArrowUp size={32} className="text-gold" weight="fill" />
          </motion.div>
          
          <h1 className="text-3xl font-heading font-semibold text-text-primary mb-2">
            Import Your Regulatory Universe
          </h1>
          <p className="text-text-secondary">
            Upload your existing regulatory universe spreadsheet. We'll import your regulations
            and you can continue to edit, add, or remove items.
          </p>
        </div>

        {/* Upload Area */}
        <div
          className={`relative border-2 border-dashed rounded-lg p-8 transition-all ${
            dragActive 
              ? 'border-gold bg-gold/5' 
              : 'border-border hover:border-gold/50'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFileSelect}
            className="hidden"
          />
          
          {!file ? (
            <div className="text-center">
              <Upload size={48} className="mx-auto text-text-tertiary mb-4" weight="light" />
              <p className="text-text-primary mb-2">
                Drag and drop your file here, or{' '}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-gold hover:underline font-medium"
                >
                  browse
                </button>
              </p>
              <p className="text-sm text-text-tertiary">
                Supported formats: Excel (.xlsx, .xls) and CSV
              </p>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              {getFileIcon(file)}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-base font-medium text-text-primary truncate">
                    {file.name}
                  </span>
                  {parseResult && (
                    <Check size={18} className="text-emerald-400 flex-shrink-0" weight="bold" />
                  )}
                </div>
                <span className="text-sm text-text-tertiary">
                  {formatFileSize(file.size)}
                </span>
                
                {uploading && (
                  <div className="mt-2 h-2 bg-background rounded-full overflow-hidden">
                    <motion.div 
                      className="h-full bg-gold"
                      initial={{ width: 0 }}
                      animate={{ width: `${uploadProgress}%` }}
                      transition={{ duration: 0.3 }}
                    />
                  </div>
                )}
                
                {parseResult && (
                  <p className="text-sm text-emerald-400 mt-1">
                    {parseResult.regulation_count} regulations imported successfully
                  </p>
                )}
              </div>
              
              {!uploading && !parseResult && (
                <button
                  type="button"
                  onClick={removeFile}
                  className="p-2 text-text-tertiary hover:text-red-400 transition-colors"
                >
                  <X size={20} />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Expected Format Info */}
        <div className="mt-6 p-4 bg-background-surface border border-border rounded-lg">
          <div className="flex items-start gap-3">
            <Info size={20} className="text-gold flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="text-text-primary font-medium mb-2">
                Expected File Format
              </p>
              <p className="text-text-secondary mb-2">
                Your file should contain columns for regulation details. We'll automatically detect:
              </p>
              <ul className="text-text-secondary space-y-1 list-disc list-inside">
                <li><strong>Name/Regulation/Legislation</strong> - The regulation name (required)</li>
                <li><strong>Regulator/Authority</strong> - Issuing authority (optional)</li>
                <li><strong>Theme/Category</strong> - Compliance area (optional)</li>
                <li><strong>Description</strong> - Details about the regulation (optional)</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Post-Upload Info */}
        <div className="mt-4 p-4 bg-gold/10 border border-gold/30 rounded-lg">
          <div className="flex items-start gap-3">
            <Sparkle size={20} className="text-gold flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="text-text-primary">
                <strong>After import, you can:</strong>
              </p>
              <ul className="text-text-secondary mt-1 space-y-1">
                <li>• Edit any regulation details</li>
                <li>• Remove regulations that don't apply</li>
                <li>• Add new legislation and frameworks from our master library</li>
                <li>• Adjust risk scores and impact ratings</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-8 flex flex-col sm:flex-row gap-4">
          <button
            type="button"
            onClick={handleUpload}
            disabled={!file || uploading || parseResult}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gold text-gold-foreground rounded-lg font-medium hover:bg-gold/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="upload-universe-btn"
          >
            {uploading ? (
              <>
                <CircleNotch size={20} className="animate-spin" />
                Processing...
              </>
            ) : parseResult ? (
              <>
                <Check size={20} weight="bold" />
                Redirecting to Universe...
              </>
            ) : (
              <>
                <Upload size={20} weight="bold" />
                Upload & Import
              </>
            )}
          </button>
          
          <button
            type="button"
            onClick={handleSkipToGenerate}
            disabled={uploading}
            className="flex items-center justify-center gap-2 px-6 py-3 border border-border rounded-lg text-text-secondary hover:bg-background-surface disabled:opacity-50 transition-colors"
          >
            <Sparkle size={20} />
            Generate New Instead
          </button>
        </div>

        {/* Skip to Dashboard */}
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            disabled={uploading}
            className="text-sm text-text-tertiary hover:text-text-secondary transition-colors"
          >
            Skip for now and go to Dashboard
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default UniverseUpload;
